// ─── Day progress ─────────────────────────────────────────────────────────────
// The single source of truth for "is this day finished?".
//
// This predicate used to be copy-pasted into NavBar, DayScreen and FullDock,
// with two looser approximations in DayDock and the Insight screen. They
// disagreed with each other, which is why a part-finished day could be flagged
// in one place and invisible in another. Everything derives from
// `getDayProgress` now — add new consumers here, not another copy.

/** The five moment text fields and the score that belongs to each. */
const MOMENT_PAIRS = [
  ["momentOne", "momentOneScore"],
  ["momentTwo", "momentTwoScore"],
  ["momentThree", "momentThreeScore"],
  ["momentFour", "momentFourScore"],
  ["momentFive", "momentFiveScore"],
];

/**
 * Is a score present?
 *
 * Presence, not truthiness. A score of 0 is a real answer: the feeling slider
 * is a controlled `type="range"`, so the string "0" only exists because the
 * user dragged it there, and the landing-page demo seeds a numeric 0. Both
 * must read as scored. Testing `!day.endScore` is the bug this replaces.
 *
 * Deliberately no NaN guard — malformed values keep whatever behaviour the
 * previous inline checks gave them. If you need a number, parse at the call
 * site.
 */
const hasScore = (value) =>
  value !== null && value !== undefined && value !== "";

/** Is a text field non-blank? */
const hasText = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

/**
 * Has the user put anything into this day at all?
 *
 * `logDate` is excluded on purpose: it is the day's identity, not its
 * substance, so a record carrying only a date is still empty.
 *
 * `objectives` is excluded too, and this one is less obvious — DayScreen seeds
 * it from the Week board on mount, so counting it would mark every brand-new
 * day as in progress.
 */
const hasAnyContent = (day) => {
  if (!day) return false;

  for (const [textKey, scoreKey] of MOMENT_PAIRS) {
    if (hasText(day[textKey])) return true;
    if (hasScore(day[scoreKey])) return true;
  }

  if (hasScore(day.startScore) || hasScore(day.endScore)) return true;

  return [
    "thankYou",
    "selfLess",
    "mindBody",
    "rememberToday",
    "leaveBehind",
    "onMyMind",
  ].some((key) => hasText(day[key]));
};

/**
 * A day is complete when it is dated, carries a feeling score, and every
 * moment the user actually wrote about has been scored.
 */
const isDayComplete = (day) => {
  if (!day?.logDate) return false;
  if (!hasScore(day.endScore)) return false;

  for (const [textKey, scoreKey] of MOMENT_PAIRS) {
    if (hasText(day[textKey]) && !hasScore(day[scoreKey])) return false;
  }

  return true;
};

/**
 * "empty" | "in-progress" | "complete"
 *
 * "empty" is the absence of a status rather than one of its own — a record
 * that exists but says nothing. Callers should render no marker for it.
 */
const getDayProgress = (day) => {
  if (isDayComplete(day)) return "complete";
  return hasAnyContent(day) ? "in-progress" : "empty";
};

/**
 * Written into, but not finished — the state the UI had no name for.
 *
 * Note this does not require `logDate`. A day with text and no date is
 * precisely the lost-day case, and it is still resumable by id.
 */
const isDayInProgress = (day) => getDayProgress(day) === "in-progress";

/** `logDate` as a comparable "YYYY-MM-DD", or "" when absent. */
const logDateKey = (day) => String(day?.logDate ?? "").slice(0, 10);

/**
 * "YYYY-MM-DD" for a Date in LOCAL time.
 *
 * Never `toISOString()`: logDate is a local date string from
 * `<input type="date">`, and UTC shifts the day either side of midnight.
 * LandingScreen already has that bug — do not copy it.
 */
const localDateKey = (date = new Date()) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate()
  ).padStart(2, "0")}`;

// Today and yesterday. Constructing the Date from components handles month,
// year and DST rollover; subtracting 86_400_000ms does not.
const recentDateKeys = (now = new Date()) => [
  localDateKey(now),
  localDateKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)),
];

/** Was this day filed under today or yesterday? */
const isRecentLogDate = (day, now = new Date()) => {
  const key = logDateKey(day);
  return key ? recentDateKeys(now).includes(key) : false;
};

/**
 * A Mongo ObjectId's first 4 bytes are its creation time in seconds.
 *
 * The day list carries `id` and nothing else — the controller drops the
 * timestamps — so this is how a day's age is known without a backend change.
 * Returns null for anything that is not a 24-character hex id.
 */
const OBJECT_ID = /^[0-9a-f]{24}$/i;
const createdAtFromId = (id) => {
  const str = String(id ?? "");
  if (!OBJECT_ID.test(str)) return null;
  const seconds = parseInt(str.slice(0, 8), 16);
  return Number.isFinite(seconds) ? new Date(seconds * 1000) : null;
};

/**
 * Newest (or oldest) first, by the date the user assigned — not the order
 * Mongo happened to return.
 *
 * `logDate` is already "YYYY-MM-DD", so string comparison is chronological.
 * Going through `new Date()` would reintroduce the timezone drift that
 * ProfileLogScreen has to dodge by appending "T12:00:00".
 */
const sortDaysByLogDate = (days, dir = "desc") => {
  if (!Array.isArray(days)) return [];

  const factor = dir === "asc" ? 1 : -1;

  // Copy first: `days` is Redux state and sort mutates in place.
  return days
    .map((day, index) => ({ day, index }))
    .sort((a, b) => {
      const keyA = logDateKey(a.day);
      const keyB = logDateKey(b.day);

      // Undated days sort last whichever way round we are going.
      if (!keyA && !keyB) return a.index - b.index;
      if (!keyA) return 1;
      if (!keyB) return -1;

      if (keyA !== keyB) return keyA < keyB ? -factor : factor;

      // Nothing stops two records sharing a logDate, so fall back to creation
      // order and let the later one win.
      return a.index - b.index;
    })
    .map(({ day }) => day);
};

/** The most recent day by date. */
const getLatestDay = (days) => sortDaysByLogDate(days, "desc")[0] ?? null;

/** The most recent part-finished day — the one to offer to resume. */
/**
 * Would the Journal button resume this day?
 *
 * Only recent days. An unfinished day from last week is the Journal Log's
 * business, not the nav's — being yanked three weeks back when you meant to
 * start today is worse than not being offered the straggler at all.
 *
 * An undated day is bounded by when it was created rather than trusted
 * outright. A day whose date field was never filled in would otherwise own the
 * nav forever, and from it "New Day" is disabled, so the nav could never reach
 * a fresh page again. Falls open when the age cannot be read.
 */
const isResumableNow = (day, now = new Date()) => {
  if (!isDayInProgress(day)) return false;
  if (logDateKey(day)) return isRecentLogDate(day, now);
  const created = createdAtFromId(day?.id);
  if (!created) return true;
  return recentDateKeys(now).includes(localDateKey(created));
};

/**
 * The one day the Journal button resumes, or null.
 *
 * This is the single source for both the button's destination and the dot
 * beside it, so the dot can never promise a resume the button does not do.
 * Scans newest-first, which sorts undated days last — a day dated today beats
 * an undated one.
 */
const findResumeTarget = (days, now = new Date()) =>
  sortDaysByLogDate(days, "desc").find((d) => isResumableNow(d, now)) ?? null;

/**
 * Written into, and still without a feeling score.
 *
 * A strict subset of isDayInProgress: a day that has a feeling score but left a
 * written moment unscored is in progress, yet not part of this backlog. This is
 * the set the loading tile counts and the log filters to.
 */
const needsFeelingScore = (day) =>
  hasAnyContent(day) && !hasScore(day?.endScore);

const countDaysNeedingScore = (days) =>
  Array.isArray(days) ? days.filter(needsFeelingScore).length : 0;

export {
  MOMENT_PAIRS,
  hasScore,
  hasText,
  hasAnyContent,
  isDayComplete,
  getDayProgress,
  isDayInProgress,
  sortDaysByLogDate,
  getLatestDay,
  localDateKey,
  isRecentLogDate,
  createdAtFromId,
  findResumeTarget,
  needsFeelingScore,
  countDaysNeedingScore,
};
